import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import className from 'classnames/bind';

import styles from './mochi-drawer.module.sass';

interface MochiDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * drawer open state
   */
  isOpen: boolean;
  /**
   * drawer is closeable or not
   */
  closeable?: boolean;
  /**
   * drawer is dark mode or not
   */
  isDarkMode?: boolean;
  /**
   * drawer content
   */
  children: React.ReactNode;
  /**
   *
   */
  rounded?: boolean;
  /**
   * callback when drawer is closed
   */
  onClose?: () => void;
  /**
   * close drawer when overlay is clicked, default is true
   */
  closeOnOverlayClick?: boolean;
  /**
   * the placement of drawer, default is bottom
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * the region covered by drawer, default is 100%
   *
   * @default '100%'
   */
  coverRegion?: string;
  /**
   * how many offsets from the top
   *
   * @default '0px'
   */
  topOffset?: string;

  /**
   * z-index of drawer
   *
   * @default 9999
   */
  zIndex?: number;
}

const cx = className.bind(styles);

/**
 * ### MochiDrawer
 *
 * @param {boolean} isOpen - drawer open state
 */

export const MochiDrawer = forwardRef<HTMLDivElement, MochiDrawerProps>(
  (props, ref) => {
    const {
      isOpen = false,
      children,
      rounded = false,
      isDarkMode = false,
      placement = 'bottom',
      coverRegion = '100%',
      topOffset = '0px',
      zIndex = 9999,
      onClose,
      closeOnOverlayClick = true,
      className,
      ...divProps
    } = props;

    const selectorRef = useRef<HTMLElement | null>(null);
    const [isClient, setIsClient] = useState(false);

    const drawerLayoutClasses = cx(className, {
      'drawer-layout': true,
      'clickable-overlay': closeOnOverlayClick,
      show: isOpen,
    });

    const drawerContainerClasses = cx({
      'drawer-container': true,
      'dark-mode': isDarkMode,
      'light-mode': !isDarkMode,
      show: isOpen,
      rounded,
      [placement]: true,
    });

    const handleClose = useCallback(() => {
      if (closeOnOverlayClick === false) return;
      onClose?.();
    }, [closeOnOverlayClick, onClose]);

    useEffect(() => {
      selectorRef.current = document.body;
    }, []);

    useEffect(() => {
      setIsClient(true);
    }, []);

    if (selectorRef.current == null || !isClient) return null;

    return createPortal(
      <div
        {...divProps}
        ref={ref}
        className={drawerLayoutClasses}
        onClick={handleClose}
        style={{
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          '--top-offset': topOffset,
          '--z-index': zIndex,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={drawerContainerClasses}
          style={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            '--custom-region': coverRegion,
            '--top-offset': topOffset,
            '--z-index': zIndex,
          }}
        >
          <div className={cx('drawer-content')}>{children}</div>
        </div>
      </div>,
      selectorRef.current,
    );
  },
);
